import {
    Button,
    Classes,
    Dialog,
    H3,
    Icon,
    Menu,
    MenuDivider,
    MenuItem,
    Popover,
    Position,
    Tooltip,
} from "@blueprintjs/core";
import classNames from "classnames";
import { JitsiConference } from "lib/lib-jitsi-meet";
import { action, observable } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { Configuration, getInvitationPathForRoom, LocalUserState, RoomState, shuffleArray } from "../../common";
import ClickToCopy from "./ClickToCopy";
import clickToCopyStyles from "./ClickToCopy.module.css";
import styles from "./DanceFloorOptions.module.css";
import { TechnicalTips } from "./WelcomeDialog";

export interface DanceFloorOptionsProps {
    room: JitsiConference;
    roomState: RoomState;
    localUser: LocalUserState;
}

@observer
export default class extends React.PureComponent<DanceFloorOptionsProps> {
    @observable public isAboutDialogOpen = false;

    public render() {
        const { roomState, room, localUser } = this.props;
        const marqueeInfo = Configuration.roomMarqueeInfo[room.getName()];

        return (
            <div className={styles.container}>
                {Configuration.SHOW_MARQUEE && typeof marqueeInfo !== "undefined" && (
                    <a className={styles.marqueeBanner} href={marqueeInfo.link} target="_blank">
                        <div className={styles.marqueeBannerText}>{marqueeInfo.text}</div>
                    </a>
                )}
                <Tooltip
                    position={Position.TOP}
                    content={`${localUser.isBottomPanelCollapsed ? "Show" : "Collapse"} bottom panel`}
                >
                    <Button icon="widget-footer" minimal={true} onClick={this.toggleBottomPanelCollapsed} />
                </Tooltip>
                <Popover minimal={true}>
                    <Button text="Dance hall options" rightIcon="more" minimal={true} />
                    <Menu>
                        <MenuItem
                            text={`${roomState.videoOrientation === "landscape" ? "Portrait" : "Landscape"} video`}
                            icon={
                                roomState.videoOrientation === "landscape" ? "horizontal-distribution" : "list-columns"
                            }
                            onClick={this.toggleVideoOrientation}
                        />
                        {/* <MenuItem
                            text="Shuffle video layout"
                            // TODO
                            disabled={true}
                            onClick={this.shuffleVideoLayout}
                        /> */}
                        <MenuItem
                            text="Reset video sizes"
                            icon="grid-view"
                            onClick={this.resetAllVideoGridDimensions}
                        />
                        <MenuItem
                            text="Toggle dance hall background"
                            icon="clean"
                            onClick={this.toggleDanceFloorBackground}
                        />
                        <MenuDivider />
                        <MenuItem text="About" icon="info-sign" onClick={this.handleFAQClick} />
                        <li>
                            <ClickToCopy
                                className={classNames(styles.invitationLink, Classes.MENU_ITEM)}
                                value={this.getInvitationLink()}
                            >
                                <Icon icon="share" /> <span className={styles.defaultInviteText}>Invite friends</span>
                                <div
                                    className={clickToCopyStyles.clipboardMessage}
                                    data-hover-message="Copy invite link"
                                />
                            </ClickToCopy>
                        </li>
                    </Menu>
                </Popover>
                <Dialog
                    isOpen={this.isAboutDialogOpen}
                    title="welcome to digital dance hall 😎"
                    onClose={this.handleDialogDismiss}
                    className={classNames(Classes.DARK, styles.aboutDialog)}
                >
                    <div className={Classes.DIALOG_BODY}>
                        <H3>What is this?</H3>
                        <p>
                            A video conferencing / live streaming platform for internet dance parties. A place to see
                            your friends dancing in their living rooms. A virtual hangout space to meet new friends
                            &amp; lovers. An antidote for social distancing for those of us who miss musical gatherings.
                        </p>
                        <H3>Where does my video go?</H3>
                        <p>
                            Video conferencing happens through the free, public{" "}
                            <a href="https://meet.jit.si/" target="_blank">
                                meet.jit.si
                            </a>{" "}
                            service. It is never recorded and conferences are destroyed once the last participant
                            leaves.
                        </p>
                        <H3>Some technical tips</H3>
                        <p>
                            <ul>
                                <li>This site works best in the latest Chrome browser.</li>
                                <li>
                                    If you don't see other participants' video, try leaving the room by clicking "Leave
                                    room" in the red button on your self video in the bottom left and then re-joining.
                                    This way, if there is an embedded audio stream playing, you can keep grooving to the
                                    music.
                                </li>
                                <TechnicalTips />
                            </ul>
                        </p>
                    </div>
                </Dialog>
            </div>
        );
    }

    @action public handleFAQClick = () => (this.isAboutDialogOpen = true);
    @action public handleDialogDismiss = () => (this.isAboutDialogOpen = false);

    @action
    private toggleVideoOrientation = () => {
        const { videoOrientation } = this.props.roomState;
        this.resetAllVideoGridDimensions();
        this.props.roomState.videoOrientation = videoOrientation === "landscape" ? "portrait" : "landscape";
    };

    @action
    private shuffleVideoLayout = () => {
        const { roomState } = this.props;
        // TODO: fix this, it throws a runtime error
        const shuffled = shuffleArray(roomState.videoLayout);
        roomState.videoLayout.clear();
        roomState.videoLayout.push(...shuffled);
    };

    @action
    private resetAllVideoGridDimensions = () => {
        this.props.roomState.videoLayout.forEach((layout) => {
            layout.gridHeight = undefined;
            layout.gridWidth = undefined;
        });
    };

    @action
    private toggleDanceFloorBackground = () => {
        this.props.roomState.enableDanceFloorBackground = !this.props.roomState.enableDanceFloorBackground;
    };

    @action
    private toggleBottomPanelCollapsed = () => {
        this.props.localUser.isBottomPanelCollapsed = !this.props.localUser.isBottomPanelCollapsed;
    };

    // HACKHACK: so janky
    private getInvitationLink() {
        if (typeof window !== "undefined") {
            const { origin } = window.location;
            return `${origin}/${getInvitationPathForRoom(this.props.room.getName())}`;
        }
    }
}
